import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context';
import { getDefaultLandingPath } from '../../utils/defaultLanding';
import './AuthCallback.css';

/**
 * Exchange an OAuth code at most once.
 * React Strict Mode remounts effects in development; Hub codes are single-use.
 */
function claimOAuthCode(code: string): boolean {
  const lockKey = `cpa_oauth_code_${code}`;
  try {
    if (sessionStorage.getItem(lockKey)) {
      return false;
    }
    sessionStorage.setItem(lockKey, '1');
    return true;
  } catch {
    return true;
  }
}

function releaseOAuthCode(code: string): void {
  const lockKey = `cpa_oauth_code_${code}`;
  try {
    sessionStorage.removeItem(lockKey);
  } catch {
    // ignore
  }
}

function goAfterAuth(): void {
  window.location.replace(getDefaultLandingPath());
}

export const AuthCallback: React.FC = () => {
  const { completeOAuth, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    const code = searchParams.get('code');

    if (isAuthenticated) {
      goAfterAuth();
      return;
    }

    if (!code) {
      setError('Missing authorization code. Please try signing in again.');
      return;
    }

    if (exchangeStartedRef.current || !claimOAuthCode(code)) {
      return;
    }
    exchangeStartedRef.current = true;

    let cancelled = false;
    completeOAuth(code)
      .then(() => {
        goAfterAuth();
      })
      .catch((err) => {
        releaseOAuthCode(code);
        exchangeStartedRef.current = false;
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Sign-in failed. Please try again.');
      });

    return () => {
      cancelled = true;
    };
  }, [completeOAuth, isAuthenticated, searchParams]);

  if (error) {
    return (
      <div className="auth-callback">
        <div className="auth-callback__card auth-callback__card--error">
          <p className="auth-callback__message">{error}</p>
          <a className="auth-callback__link" href="/login">
            Back to sign in
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-callback">
      <div className="auth-callback__card">
        <div className="auth-callback__spinner" />
        <p className="auth-callback__message">Completing sign-in…</p>
      </div>
    </div>
  );
};

export default AuthCallback;
